// The confidence band (#1141). The model's `root_cause_confidence` is its own
// estimate, not a measured probability, so the console shows a band and the
// recorded reasons, never the number. The number stays a number on the wire.

import type { Conclusion, RunProjection, TimelineRow } from "../features/ops/investigationProjection";

/** The cut-offs, stated once (owner, 2026-10-08, #480): High from 0.8, Moderate
 *  from 0.5, Low below. A presentation choice, not a calibration. Re-pin when
 *  the first accepted reading holds enough wrong conclusions to compare stated
 *  confidence against correctness. */
const HIGH_FROM = 0.8;
const MODERATE_FROM = 0.5;

export const CONFIDENCE_HELP =
  "This band is the investigation's own estimate of how sure it is. It is not a measured rate of being right.";

/** A refused conclusion has no band; an absent number has none to show. */
export function confidenceBand(conclusion: Conclusion): string {
  if (conclusion.conclusion_status === "refused") return "No conclusion";
  const stored = conclusion.root_cause_confidence;
  if (stored === null) return "Unknown";
  if (stored >= HIGH_FROM) return "High";
  if (stored >= MODERATE_FROM) return "Moderate";
  return "Low";
}

/** Each reason only when the run's record holds it; never inferred from prose. */
export function confidenceReasons(projection: RunProjection, conclusion: Conclusion): string[] {
  if (conclusion.conclusion_status === "refused") return [];
  const reasons: string[] = [];
  if (conclusion.alert_contradiction) reasons.push("The alert's own text contradicts this conclusion.");
  if (conclusion.unverified_breach) reasons.push("A breach this conclusion rests on was not independently verified.");
  if (conclusion.mechanism_gap) reasons.push("The conclusion states a gap in its mechanism.");
  if (projection.findings.some((finding) => finding.retraction)) {
    reasons.push("A finding was retracted after the conclusion.");
  }
  return reasons;
}

/** One plain sentence per refusal rule, written for the reader. The rule is the runtime's exception
 *  name, as `ConcludeHook` records it (`agent/hooks.py`); the rules `conclude` can raise are
 *  `ConcludeAbsenceRefused`, `ConcludeAbsenceNudge` (`agent/absence.py`), `ConcludeMechanismSplitError`,
 *  `ConcludeMechanismActorNudge` (`agent/mechanism_actor.py`), `ConcludeUnverifiedMechanismNudge`
 *  (`agent/unverified_mechanism.py`), `ConcludeAlertAssessmentError` (`agent/alert_assessment.py`) and the
 *  schema's `ValueError`. The verbatim reason is model-facing text and lives only behind the evidence link. */
const RULE_SENTENCES: Readonly<Record<string, string>> = {
  ConcludeAbsenceRefused: "A claim that something was ruled out did not rest on a read that ran.",
  ConcludeAbsenceNudge: "A claim of absence was stated in prose rather than as a checked claim.",
  ConcludeMechanismSplitError: "The stated mechanism and the stated gap did not fit together.",
  ConcludeAlertAssessmentError: "The conclusion did not say how the alert's own text bears on it.",
  ConcludeMechanismActorNudge: "The mechanism repeated the trigger's actor instead of stating a mechanism.",
  ConcludeUnverifiedMechanismNudge: "The conclusion called its own mechanism unverified.",
  ValueError: "The conclusion's fields did not match the required shape.",
};
const UNKNOWN_RULE = "The conclusion was refused by one of the investigation's checks.";
const NO_REASON = "The record holds no reason for the refusal.";

/** What a refused run says beside "No conclusion" (#1434), read from its record. */
export interface RefusedConclusion {
  /** Which of the runtime's two refused endings this is. */
  ending: "no_cause" | "cause_kept";
  headline: string;
  /** One plain sentence for the last `conclude` refusal's rule. Never the verbatim reason. */
  explanation: string;
  /** The refusal record the verbatim reason is read from, for the link to its evidence. */
  row: TimelineRow | null;
  /** The claims the runtime demoted to untested, by their own statement. */
  unverified: string[];
  limits: string[];
  /** A plain heading for a no-cause run, in place of the runtime's own `root_cause` sentence. */
  heading: string | null;
  /** True when a conclusion text carries the runtime's embedded last refusal (the gap it writes when
   *  no mechanism stands, and the summary that falls back to it): such text is the runtime's, not the
   *  investigation's, and the Summary does not show it. The complete record's `absence_demotions`
   *  marker identifies an investigation-written gap, even when it quotes a refusal. Otherwise use
   *  the recorded reason only when long enough to distinguish it from ordinary model text; short
   *  or absent reasons use the runtime wording pinned in `tests/test_agent_conclude.py`. */
  embedsRefusal: (text: string | null) => boolean;
}

/** Null unless the conclusion was refused. The mechanism tells the endings apart, as `refusalNotice` does. */
export function refusedConclusion(projection: RunProjection, conclusion: Conclusion): RefusedConclusion | null {
  if (conclusion.conclusion_status !== "refused") return null;
  const refusals = projection.timeline.filter(
    (row) => row.type === "tool_refused" && row.tool_name === "conclude",
  );
  const row = refusals.length > 0 ? refusals.reduce((a, b) => (b.seq > a.seq ? b : a)) : null;
  const rule = row?.payload.rule;
  const explanation = row
    ? typeof rule === "string" && Object.prototype.hasOwnProperty.call(RULE_SENTENCES, rule)
      ? RULE_SENTENCES[rule]
      : UNKNOWN_RULE
    : NO_REASON;
  const kept = Boolean(conclusion.mechanism);
  // `hooks.py::refused_conclusion` marks the standing ending; the no-cause ending never does.
  // Read presence from the terminal payload, including an empty list, rather than infer ownership
  // from the gap's prose. The projection retains the whole payload on its timeline row.
  const terminals = projection.timeline.filter((entry) => entry.type === "complete");
  const terminal = terminals[terminals.length - 1];
  const investigationWritten = terminal !== undefined &&
    Object.prototype.hasOwnProperty.call(terminal.payload, "absence_demotions");
  const recorded = typeof row?.payload.reason === "string" ? row.payload.reason.trim().slice(0, 120) : "";
  const embedsRefusal = (text: string | null): boolean =>
    !investigationWritten && text !== null &&
    (recorded.length >= 40 ? text.includes(recorded) : /the last refusal \(/.test(text));
  const limits: string[] = [];
  if (conclusion.alert_contradiction) {
    limits.push("Check: the alert's own text contradicts the stated cause.");
  }
  if (conclusion.unverified_breach) {
    limits.push("Check: a breach the cause rests on was not independently verified.");
  }
  if (conclusion.mechanism_gap && investigationWritten) {
    limits.push("Check: the investigation states a gap in its mechanism.");
  }
  if (projection.findings.some((finding) => finding.retraction)) {
    limits.push("Check: a finding was retracted after the conclusion.");
  }
  return {
    ending: kept ? "cause_kept" : "no_cause",
    headline: kept
      ? "Cause kept; some exclusions could not be verified."
      : "No cause established: the investigation would not commit to one.",
    explanation,
    row,
    unverified: (conclusion.alternatives_considered ?? [])
      .filter((claim) => claim.demoted && claim.disposition === "untested")
      .map((claim) => claim.statement),
    limits,
    heading: kept ? null : "No cause was established",
    embedsRefusal,
  };
}
